import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { BlobField } from "./BlobField";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RECIPIENT_EMAIL = "subharuphazra76@gmail.com";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success(res.data?.message || "Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      setErrorMsg(String(msg));
      toast.error(String(msg));
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <BlobField variant="ambient" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          {/* Left: heading + contact meta */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="md:col-span-5"
          >
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-emerald-300/80 mb-5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Say hello
            </div>
            <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98] mb-8">
              Let&apos;s build <br />
              <span className="italic font-normal text-emerald-300">something rare.</span>
            </h2>
            <p className="text-white/65 text-base md:text-lg leading-relaxed max-w-md mb-10">
              Have a product in mind or a stubborn problem to solve? Send a note. We reply within one working day.
            </p>

            <div className="space-y-5">
              <a
                href={`mailto:${RECIPIENT_EMAIL}`}
                className="group flex items-start gap-4 text-white/80 hover:text-white transition-colors"
              >
                <span className="mt-1 grid place-items-center w-9 h-9 rounded-full border border-emerald-400/30 text-emerald-300">
                  <Mail size={15} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-1">Email</div>
                  <div className="link-underline text-base md:text-lg">{RECIPIENT_EMAIL}</div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="mt-1 ml-1 text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </a>
              <div className="flex items-start gap-4 text-white/70">
                <span className="mt-1 grid place-items-center w-9 h-9 rounded-full border border-white/10">
                  <MapPin size={15} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-1">Studio</div>
                  <div>Remote-first, worldwide.</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: glass form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="md:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid={CONTACT.form}
              className="relative glass rounded-3xl p-6 md:p-10"
            >
              {/* corner blob */}
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-56 h-56 opacity-60 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(0,208,132,0.35), transparent 70%)",
                  filter: "blur(40px)",
                  borderRadius: "50% 50% 40% 60%",
                }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 relative">
                <Field
                  label="Name"
                  name="name"
                  testId={CONTACT.name}
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your full name"
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  testId={CONTACT.email}
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@company.com"
                />
                <div className="md:col-span-2">
                  <Field
                    label="Subject"
                    name="subject"
                    testId={CONTACT.subject}
                    value={form.subject}
                    onChange={onChange}
                    placeholder="What's the project?"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.24em] text-white/50 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    data-testid={CONTACT.message}
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    placeholder="Tell us what you have in mind - budgets, timelines, the sharp problem you want solved."
                    className="w-full bg-black/25 border border-white/10 focus:border-emerald-400/60 rounded-xl px-4 py-3 text-white placeholder-white/30 outline-none transition-colors resize-none"
                    required
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative">
                <p className="text-xs text-white/40 max-w-sm">
                  By submitting, you agree we may contact you about your enquiry. We never share your details.
                </p>
                <button
                  type="submit"
                  data-testid={CONTACT.submit}
                  disabled={status === "loading"}
                  className="group inline-flex items-center gap-3 rounded-full bg-emerald-400 text-emerald-950 pl-6 pr-2 py-2 hover:bg-emerald-300 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="text-sm font-medium tracking-wide">
                    {status === "loading" ? "Sending…" : "Send message"}
                  </span>
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-950 text-emerald-300 group-hover:translate-x-1 transition-transform">
                    <Send size={15} />
                  </span>
                </button>
              </div>

              {status === "success" && (
                <p
                  data-testid={CONTACT.successMsg}
                  className="mt-5 text-sm text-emerald-300"
                >
                  Message received — we'll reply to your inbox within one working day.
                </p>
              )}
              {status === "error" && (
                <p className="mt-5 text-sm text-red-300/90">{errorMsg}</p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", testId, value, onChange, placeholder }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.24em] text-white/50 mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      data-testid={testId}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={name !== "subject"}
      className="w-full bg-black/25 border border-white/10 focus:border-emerald-400/60 rounded-xl px-4 py-3 text-white placeholder-white/30 outline-none transition-colors"
    />
  </div>
);
